/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description  fhir parser utils for eligibility
 */

import _ from 'lodash';
import { renderDate, renderDateRange } from 'components/eligibility/response/RenderSchema';
import { DTP_DATES, QUANTITY_QUALIFIER_MAPPING, TIME_QUALIFIER_MAPPING } from 'app-constants/eligibility/benefit';
import systemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import {
	labelMapping,
	codeMapping,
	serviceTypeDisplayMapping,
	hiSegmentMapping,
	mpiSegmentMapping,
	modifiersSystem,
	diagnosisCodesSystem,
} from 'app-constants/eligibility/fhirLabelMapping';

// function to get a resource
const getResource = (entries: Fhir.BundleEntry[], valueReference?: string): Fhir.BundleEntry | undefined =>
	_.find(entries, (item) => !!item?.resource?.id && _.includes(valueReference, item?.resource?.id));

// function to get fhir coverage response entry
const getFhirCoverageResponse = (entries: Fhir.BundleEntry[]): Fhir.CoverageEligibilityResponse | undefined => {
	const coverageResponse = _.find(entries, (entry) => entry?.resource?.resourceType === 'CoverageEligibilityResponse');
	if (coverageResponse) {
		return coverageResponse.resource as Fhir.CoverageEligibilityResponse;
	}
};

// function to get coverage path
const getCoveragePath = (
	productOrService?: EligibilityResponse.IParsedCoding,
	benefitType?: string,
	network?: string,
	coverage?: string,
): string[] => {
	return [
		`${productOrService?.display} - ${productOrService?.code}`,
		`${benefitType} - ${productOrService?.display}`,
		`${network}`,
		`${coverage}`,
	];
};
// function to get human name
const getHumanName = (names: Fhir.HumanName[]): { firstName: string; lastName: string; name: string } => {
	const parsed = _.first(names);
	const firstName = _.join(_.compact(parsed?.given), ' ');
	const lastName = parsed?.family || '';
	const name = `${firstName} ${lastName}`;
	return { firstName, lastName, name };
};

// function to get stringified address
const getAddress = (addresses: Fhir.Address): string => {
	return _.join(
		_.compact(
			_.concat(addresses?.line, [addresses?.city, addresses?.district, addresses?.state, addresses?.country, addresses?.postalCode]),
		),
		', ',
	);
};

// function to get amount
const getAmount = (
	amount?: number,
	currency?: string,
	valuePercent?: string,
	valueQuantity?: string,
	amountQualifier?: string,
): EligibilityResponse.ICoverageAmountMeta[] => {
	let amountMeta = [] as EligibilityResponse.ICoverageAmountMeta[];
	if (_.isNumber(amount)) {
		amountMeta = [
			{
				amountDetails: _.compact([_.toString(amount), currency, amountQualifier]).join(' '),
				shouldShowPaymentsButton: true,
			},
		];
	} else if (valuePercent) {
		amountMeta = [
			{
				amountDetails: _.compact([valuePercent, amountQualifier]).join(' '),
				shouldShowPaymentsButton: false,
			},
		];
	}
	if (valueQuantity) {
		amountMeta.push({
			amountDetails: _.compact([valueQuantity, amountQualifier]).join(' '),
			shouldShowPaymentsButton: false,
		});
	}
	return amountMeta;
};

// function to pick coding
const pickCoding = (coding?: Fhir.Coding): EligibilityResponse.IParsedCoding => {
	return {
		code: coding?.code || '',
		display: coding?.display || '',
	};
};

// function to get contacts
const getContacts = (contacts: Fhir.Contacts[]): Record<string, string> => {
	const contactsList = {} as Record<string, string>;
	const telecomContact = _.find(contacts, (obj) => _.has(obj, 'telecom')) || ({} as Fhir.Contacts);
	const telecomList = telecomContact?.telecom || [];
	_.map(telecomList, (telecom) => {
		const system = telecom?.system;
		const value = telecom?.value;
		if (system && value) {
			// Here, we append each value to the contactsList using the system as the key
			contactsList[system] = value;
		}
	});
	return contactsList;
};

// function to get reference meta from extensions
const getReferenceMeta = (extensions?: Fhir.Extension[]): EligibilityResponse.ItemType[] => {
	const referenceMeta = [] as EligibilityResponse.ItemType[];
	_.map(extensions, (extension) => {
		if (_.includes(extension.url, systemMapping['REF-02'])) {
			const display = _.first(extension?.valueIdentifier?.type?.coding)?.display;
			const text = extension?.valueIdentifier?.type?.text;
			const value = extension?.valueIdentifier?.value;
			if (display && value) {
				referenceMeta.push({
					label: display,
					value: text ? `${value} (${text})` : value,
				});
			}
		}
	});
	return referenceMeta;
};

// function to get identifier meta from extensions
const getIdentifierMeta = (identifier: Fhir.Identifier[]): EligibilityResponse.ItemType[] => {
	const identifierMeta = [] as EligibilityResponse.ItemType[];
	_.map(identifier, (item) => {
		const codingList = item?.type?.coding;
		const text = item?.type?.text;
		if (codingList) {
			const firstCodingItem = codingList[0];
			const secondCodingItem = codingList[1];
			const label = secondCodingItem ? secondCodingItem?.display : firstCodingItem?.display;
			const value = item?.value;
			if (value && label) {
				identifierMeta.push({
					label,
					value: text ? `${value} (${text})` : value,
				});
			}
		}
	});
	return identifierMeta;
};
// function to get dates meta from extensions
const getDatesMeta = (extensions?: Fhir.Extension[]): EligibilityResponse.ItemType[] => {
	const datesMeta = [] as EligibilityResponse.ItemType[];
	_.map(extensions, (extension) => {
		if (_.includes(extension?.url, systemMapping['DTP-03'])) {
			const code = _.replace(extension?.url, `${systemMapping['DTP-03']}/`, '');
			const dtpKey = _.get(DTP_DATES, code);
			if (dtpKey) {
				if (extension?.valuePeriod) {
					datesMeta.push({
						label: dtpKey,
						value: renderDateRange(extension?.valuePeriod?.start, extension?.valuePeriod?.end),
					});
				} else if (extension?.valueDateTime) {
					datesMeta.push({
						label: dtpKey,
						value: renderDate(extension?.valueDateTime),
					});
				} else if (extension?.valueDate) {
					datesMeta.push({
						label: dtpKey,
						value: renderDate(extension?.valueDate),
					});
				}
			}
		}
	});
	return datesMeta;
};

// function to get eb sections data
const getEbSectionData = (extensions?: Fhir.Extension[]): EligibilityResponse.IEbSegmentDetails => {
	const details = [] as EligibilityResponse.ItemType[];
	const ebSegmentDetails = {} as EligibilityResponse.IEbSegmentDetails;

	_.forEach(extensions, (extension) => {
		const modifiers = [] as string[];
		const diagnosisCodes = [] as string[];
		/**
		 * case  insurance type
		 */
		const insuranceTypeCoding = _.find(extension?.valueCodeableConcept?.coding, (item) =>
			_.includes(item?.system, systemMapping['EB-04']),
		);
		if (insuranceTypeCoding) {
			details.push({
				label: labelMapping.insuranceType,
				value: insuranceTypeCoding?.display || '',
			});
		}

		/**
		 * case  authorization EB11
		 */
		const authTypeCoding = _.find(extension?.valueCodeableConcept?.coding, (item) => _.includes(item?.system, systemMapping['EB-11']));
		if (authTypeCoding?.code) {
			ebSegmentDetails.authRequired = authTypeCoding?.code;
		}
		/**
		 * case amount qualifier EB06
		 */
		const qualifierCoding = _.find(extension?.valueCodeableConcept?.coding, (item) => _.includes(item?.system, systemMapping['EB-06']));
		if (qualifierCoding?.code && qualifierCoding?.display) {
			const display = _.get(TIME_QUALIFIER_MAPPING, qualifierCoding?.code)?.display || qualifierCoding?.display;
			ebSegmentDetails.amountQualifier = display;
		}

		/**
		 * case  EB13, EB14
		 */
		const procedureType = _.find(extension?.valueCodeableConcept?.coding, (item) =>
			_.includes(item?.system, systemMapping['EB-13-01']),
		)?.code;
		const procedureCode = _.find(extension?.valueCodeableConcept?.coding, (item) =>
			_.includes(item?.system, systemMapping['EB-13-02']),
		)?.code;
		if (procedureType && procedureCode) {
			details.push({
				label: labelMapping.procedureCode,
				value: `${procedureType}-${procedureCode}`,
			});
		}

		/**
		 * case  EB08, EB10
		 */
		if (extension?.url === systemMapping['EB-08'] && extension?.valueRatio) {
			const percent =
				(_.toNumber(extension?.valueRatio?.numerator?.value) || 0) / (_.toNumber(extension?.valueRatio?.denominator?.value) || 100);

			ebSegmentDetails.valuePercent = `${_.round(percent * 100, 2)} Percent`;
		}
		if (extension?.url === systemMapping['EB-10'] && extension?.valueQuantity) {
			const value = extension?.valueQuantity?.value;
			const code = extension?.valueQuantity?.code;
			const unit = extension?.valueQuantity?.unit;

			if (value && code && unit) {
				const display = _.get(QUANTITY_QUALIFIER_MAPPING, code)?.display || unit;
				ebSegmentDetails.valueQuantity = `${value} ${display}`;
			}
		}

		if (extension?.url === systemMapping.EB) {
			const codingList = extension?.valueCodeableConcept?.coding || [];
			_.map(codingList, (coding) => {
				if (_.includes(modifiersSystem, coding?.system) && coding?.code) {
					modifiers.push(coding?.code);
				}
				if (_.includes(diagnosisCodesSystem, coding?.system) && coding?.code) {
					diagnosisCodes.push(coding?.code);
				}
			});
			details.push({
				label: labelMapping.modifiers,
				value: _.toString(modifiers),
			});
			details.push({
				label: labelMapping.diagnosisCodes,
				value: _.toString(diagnosisCodes),
			});

			if (extension?.valueCodeableConcept?.text) {
				details.push({
					label: labelMapping.planDescription,
					value: extension?.valueCodeableConcept?.text || '',
				});
			}
		}
	});
	ebSegmentDetails.details = details;
	return ebSegmentDetails;
};

// function to get place of service
const getPlaceOfService = (extensions?: Fhir.Extension[]): EligibilityResponse.ItemType[] => {
	const details = [] as EligibilityResponse.ItemType[];
	_.forEach(extensions, (extension) => {
		/**
		 * case place of service
		 */
		if (extension?.url === systemMapping.III) {
			const III_01 = _.find(extension?.valueCodeableConcept?.coding, (item) => _.includes(item?.system, systemMapping['III-01']));
			const III_02 = _.find(extension?.valueCodeableConcept?.coding, (item) => _.includes(item?.system, systemMapping['III-02']));
			const III_03 = _.find(extension?.valueCodeableConcept?.coding, (item) => _.includes(item?.system, systemMapping['III-03']));
			if (III_01?.code && III_01?.display && III_02?.display) {
				details.push({
					label: _.get(serviceTypeDisplayMapping, III_01?.code, III_01?.display),
					value: III_02?.display,
				});
			}
			if (III_03?.code && III_03?.display) {
				details.push({
					label: _.get(serviceTypeDisplayMapping, III_03?.code, III_03?.display),
					value: extension?.valueCodeableConcept?.text || '',
				});
			}
		}
	});
	// service place and injury type can be more than once so group them together
	return _.map(_.groupBy(details, 'label'), (group, label) => ({
		label,
		value: _.size(group) === 1 ? group[0]?.value : _.map(group, 'value').join(' | '),
	}));
};

// function to get notes meta from extensions
const getNotesMeta = (extensions?: Fhir.Extension[]): EligibilityResponse.ItemType[] => {
	const payerNotes: string[] = [];
	const serviceDeliveryNotes: string[] = [];
	const providerInformation: string[] = [];

	_.map(extensions, (extension) => {
		// msg section
		if (_.includes(systemMapping['MSG-01'], extension?.url)) {
			payerNotes.push(extension?.valueString || '');
		}
		// hsd section
		if (_.includes(systemMapping.HSD, extension?.url)) {
			serviceDeliveryNotes.push(extension?.valueCodeableConcept?.text || '');
		}
		// prv section
		if (_.includes(systemMapping.PRV, extension?.url)) {
			let prv1;
			let prv2;
			let prv3;
			_.map(extension?.valueCodeableConcept?.coding, (coding) => {
				if (_.includes(systemMapping['PRV-01'], coding?.system)) {
					prv1 = `Type: ${coding?.display}`;
				}
				if (_.includes(systemMapping['PRV-02'], coding?.system)) {
					prv2 = coding?.display;
				}
				if (_.includes(systemMapping['PRV-03'], coding?.system)) {
					prv3 = coding?.code;
				}
			});
			providerInformation.push(prv1 || '');
			if (prv2 && prv3) {
				providerInformation.push(`${prv2}: ${prv3}`);
			}
		}
	});
	return [
		{
			label: labelMapping.serviceDeliveryInfo,
			style: { backgroundColor: '#f0e6f2', borderColor: '#aea0b4' },
			value: _.compact(serviceDeliveryNotes),
		},
		{
			label: labelMapping.providerInfo,
			style: { backgroundColor: '#edf2f0', borderColor: '#adbbb6' },
			value: _.compact(providerInformation),
		},
		{
			label: labelMapping.payerNotes,
			style: { backgroundColor: '#e8f6ff', borderColor: '#a2daff' },
			value: _.compact(payerNotes),
		},
	];
};

// get mpi details

const getMpiDetails = (extensions?: Fhir.Extension[]): EligibilityResponse.ItemType[] => {
	const mpiSegment = _.find(extensions, (extension) => extension?.url === systemMapping.MPI);
	const mpiItems = [] as EligibilityResponse.ItemType[];
	if (mpiSegment) {
		const codingItems = mpiSegment?.valueCodeableConcept?.coding || [];
		// mpi details(information status, employment status, rank etc)
		_.map(codingItems, (item) => {
			const label = _.get(mpiSegmentMapping, item?.system || '');
			const value = item?.display;
			if (label && value) {
				mpiItems.push({
					label,
					value,
				});
			}
		});
		// mpi description
		const mpiDescription = mpiSegment?.valueCodeableConcept?.text;
		if (mpiDescription) {
			mpiItems.push({
				label: labelMapping.mpiDescription,
				value: mpiDescription,
			});
		}
	}
	// service span
	const serviceSpan = _.find(extensions, (extension) => extension.url === systemMapping['MPI-07']);
	if (serviceSpan) {
		const label = labelMapping.serviceSpan;
		if (serviceSpan?.valuePeriod) {
			mpiItems.push({
				label,
				value: renderDateRange(serviceSpan?.valuePeriod?.start, serviceSpan?.valuePeriod?.end),
			});
		} else if (serviceSpan?.valueDateTime) {
			mpiItems.push({
				label,
				value: renderDate(serviceSpan?.valueDateTime),
			});
		} else if (serviceSpan?.valueDate) {
			mpiItems.push({
				label,
				value: renderDate(serviceSpan?.valueDate),
			});
		}
	}
	return mpiItems;
};

// ins details

const getInsMeta = (extensions?: Fhir.Extension[]): EligibilityResponse.ItemType[] => {
	const details = [] as EligibilityResponse.ItemType[];
	_.forEach(extensions, (extension) => {
		/**
		 * case  relation type
		 */
		const relationTypeCoding = _.find(extension?.valueCodeableConcept?.coding, (item) =>
			_.includes(item?.system, systemMapping['INS-02']),
		);

		const birthSequenceTypeCoding = _.find(extension?.valueCodeableConcept?.coding, (item) =>
			_.includes(item?.system, systemMapping['INS-17']),
		);

		if (relationTypeCoding) {
			details.push({
				label: labelMapping.relationship,
				value: relationTypeCoding?.display || '',
			});
		}
		if (birthSequenceTypeCoding) {
			details.push({
				label: labelMapping.birthSeq,
				value: birthSequenceTypeCoding?.display || '',
			});
		}
	});
	return details;
};

// function that will return everything inside a resource, no fixed typing for resource, it can be any FHIR resource
const getResourceDetails = (resource: AnyType): EligibilityResponse.ItemType[] => {
	let resourceDetails = [] as EligibilityResponse.ItemType[];
	const address = resource?.address ?? [];
	const telecom = resource?.telecom ?? [];
	const contact = resource?.contact ?? [];
	const identifier = resource?.identifier ?? ([] as Fhir.Identifier[]);
	const type = resource?.type?.[0]?.coding?.[0]?.display;
	// type
	if (type) {
		resourceDetails.push({
			label: labelMapping.type,
			value: type,
		});
	}
	// name
	if (resource?.name) {
		if (_.isString(resource?.name)) {
			resourceDetails.push({
				label: labelMapping.name,
				value: resource?.name,
			});
		} else {
			const nameMeta = getHumanName(resource?.name || []);
			resourceDetails.push(
				{
					label: labelMapping.firstName,
					value: nameMeta?.firstName,
				},
				{
					label: labelMapping.lastName,
					value: nameMeta?.lastName,
				},
				{
					label: labelMapping.name,
					value: nameMeta?.name,
				},
			);
		}
	}
	// gender
	if (resource?.gender) {
		resourceDetails.push({
			label: labelMapping.gender,
			value: _.startCase(resource?.gender),
		});
	}
	// birth date
	if (resource?.birthDate) {
		resourceDetails.push({
			label: labelMapping.dob,
			value: renderDate(resource?.birthDate),
		});
	}

	// for patient type of resource
	if (!_.isEmpty(address)) {
		resourceDetails.push({
			label: labelMapping.address,
			value: getAddress(_.first(address) || {}),
		});
	}
	if (!_.isEmpty(telecom)) {
		resourceDetails.push({
			label: labelMapping.contact,
			value: getContacts([{ telecom }]),
		});
	}

	// organization contact points - consolidated into a single "Contacts" section
	if (!_.isEmpty(contact)) {
		const contactsMap: Record<string, AnyType> = {};
		let contactIndex = 1;

		_.forEach(contact as Fhir.Contacts[], (c) => {
			const contactEntry: Record<string, string | Record<string, string>> = {};
			if (c?.purpose) {
				const purposeDisplay = _.first(c.purpose.coding)?.display || c.purpose.text;
				if (purposeDisplay) {
					contactEntry.purpose = purposeDisplay;
				}
			}
			if (c?.name) {
				const nameMeta = getHumanName(c.name);
				if (nameMeta?.name?.trim()) {
					contactEntry.name = nameMeta.name.trim();
				}
			}
			if (c?.address) {
				const addr = getAddress(c.address);
				if (addr) {
					contactEntry.address = addr;
				}
			}
			if (!_.isEmpty(c?.telecom)) {
				const telecomData = getContacts([c]);
				if (!_.isEmpty(telecomData)) {
					contactEntry.telecom = telecomData;
				}
			}
			if (c?.period) {
				contactEntry.period = renderDateRange(c.period.start, c.period.end);
			}
			if (!_.isEmpty(contactEntry)) {
				contactsMap[`Contact ${contactIndex}`] = contactEntry;
				contactIndex++;
			}
		});

		if (!_.isEmpty(contactsMap)) {
			resourceDetails.push({
				label: labelMapping.contact,
				value: contactsMap,
			});
		}
	}
	const identifierMeta = getIdentifierMeta(identifier);
	const referenceMeta = getReferenceMeta((resource?.extension || []) as Fhir.Extension[]);
	const placeOfService = getPlaceOfService((resource?.extension || []) as Fhir.Extension[]);
	const datesMeta = getDatesMeta((resource?.extension || []) as Fhir.Extension[]);
	const insMeta = getInsMeta((resource?.extension || []) as Fhir.Extension[]);
	const mpiMeta = getMpiDetails((resource?.extension || []) as Fhir.Extension[]);
	const notesMeta = getNotesMeta((resource?.extension || []) as Fhir.Extension[]);
	// return all possible data a Resource can have
	resourceDetails = _.concat(resourceDetails, datesMeta, identifierMeta, referenceMeta, insMeta, placeOfService, mpiMeta, notesMeta);
	return resourceDetails;
};

// function to get time qualifier
const getTimeQualifier = (extensions: Fhir.Extension[]): string | undefined => {
	const codes = _.compact(
		_.flatMap(extensions, (extension) => {
			if (extension?.url === systemMapping.EB) {
				return _.map(extension?.valueCodeableConcept?.coding, (coding) => {
					if (coding?.system === systemMapping['EB-06']) {
						return coding?.code;
					}
					return undefined;
				});
			}
			return undefined;
		}),
	);
	/**
	 * if no qualifier, it should always be at top position of sorting
	 * so return 1 as other codes number is >1
	 */
	return _.head(codes) || '1';
};

// function to get time qualifier
const getInsuranceType = (extensions: Fhir.Extension[]): string | undefined => {
	const codes = _.compact(
		_.flatMap(extensions, (extension) => {
			if (extension?.url === systemMapping.EB) {
				return _.map(extension?.valueCodeableConcept?.coding, (coding) => {
					if (coding?.system === systemMapping['EB-04']) {
						return coding?.display;
					}
					return undefined;
				});
			}
			return undefined;
		}),
	);

	return _.head(codes);
};

const flattenBenefit = (items: Fhir.CoverageEligibilityResponseInsuranceItem[]): Fhir.CoverageEligibilityResponseInsuranceItem[] => {
	return _.flatMap(items, (item) => {
		return _.map(item?.benefit, (benefitItem) => {
			return {
				...item,
				benefit: [benefitItem],
				timeQualifier: getTimeQualifier(benefitItem?.extension || []),
				insuranceType: getInsuranceType(benefitItem?.extension || []),
			};
		});
	});
};

// function to sort benefits data
const getSortedBenefitData = (
	insuranceData: Fhir.CoverageEligibilityResponseInsuranceItem[],
	sortOrder: string[],
): Fhir.CoverageEligibilityResponseInsuranceItem[] => {
	const sortedServiceType = _.uniq(_.concat(['30'], sortOrder));
	const groupedData = _.groupBy(insuranceData, ({ productOrService }) => _.get(_.first(productOrService?.coding), 'code'));
	const keys = _.keys(groupedData);
	const unSelectedKeys = _.difference(keys, sortedServiceType);
	const allKeys = _.concat(sortedServiceType, unSelectedKeys);

	const sortedServiceBenefits = _.mapValues(groupedData, (value) => {
		return _.sortBy(flattenBenefit(value), [
			['benefit[0].type.coding[0].code'],
			['network.coding[0].code'],
			['unit.coding[0].code'],
			['insuranceType'],
			['timeQualifier'],
		]);
	}) as Record<string, Fhir.CoverageEligibilityResponseInsuranceItem[]>;

	return _.flattenDeep(_.map(allKeys, (key) => _.get(sortedServiceBenefits, key)));
};

// function that will return secondary, tertiary and primary care providers data
const getSecondaryTertiaryPayerAndPrimaryCareProviderDetails = (entries: Fhir.BundleEntry[]): EligibilityResponse.IAdditionalInfo => {
	const primaryCareProviders = [] as EligibilityResponse.ItemType[][];
	let secondaryPayer = [] as EligibilityResponse.ItemType[];
	let tertiaryPayer = [] as EligibilityResponse.ItemType[];
	const typePath = 'type.0.coding.0.code';
	const providerEntries = _.filter(entries, (entry) => {
		const resourceType = _.get(entry?.resource, typePath);
		return resourceType === codeMapping.primaryCareProvider || resourceType === codeMapping.primaryCareProviderEB;
	});
	const secondaryPayerEntries = _.find(entries, (entry) => _.get(entry?.resource, typePath) === codeMapping.secondaryPayer);
	const tertiaryPayerEntries = _.find(entries, (entry) => _.get(entry?.resource, typePath) === codeMapping.tertiaryPayer);
	if (!_.isEmpty(providerEntries)) {
		_.map(providerEntries, (entry) => {
			const entryDetails = getResourceDetails(entry?.resource);
			primaryCareProviders.push(entryDetails);
		});
	}
	if (secondaryPayerEntries) {
		secondaryPayer = getResourceDetails(secondaryPayerEntries?.resource);
	}
	if (tertiaryPayerEntries) {
		tertiaryPayer = getResourceDetails(tertiaryPayerEntries?.resource);
	}
	return { secondaryPayer, tertiaryPayer, primaryCareProviders };
};

// function that will return additional resources present in a benefit extension
const getOtherExtensionData = (entries: Fhir.BundleEntry[], extensions?: Fhir.Extension[]): EligibilityResponse.IOtherExtensionData => {
	const additionalDetails = {} as EligibilityResponse.IOtherExtensionData;
	const additionalReferences = [] as EligibilityResponse.ItemType[][];
	_.forEach(extensions, (extension) => {
		// ********************** other resource **********************
		if (_.includes(extension?.url, systemMapping.AdditionalResourceReference)) {
			const resource = getResource(entries, extension?.valueReference?.reference)?.resource;
			if (resource) {
				const resourceDetails = getResourceDetails(resource);
				additionalReferences.push(resourceDetails);
			}
		}
	});
	additionalDetails.additionalReferences = additionalReferences;
	return additionalDetails;
};

// function to update benefit description and disclaimer
const updateBenefitDescriptionAndDisclaimer = (
	descriptions: string[],
	disclaimers: string[],
	isBenefitDescription: boolean,
	isBenefitDisclaimer: boolean,
	notesMeta: EligibilityResponse.ItemType[],
): { descriptions: string[]; disclaimers: string[] } => {
	const payerNotes = _.first(_.filter(notesMeta, (note) => note?.label === labelMapping.payerNotes));

	if (isBenefitDescription && !_.isEmpty(payerNotes?.value)) {
		descriptions = _.concat(descriptions, payerNotes.value as string);
	} else if (isBenefitDisclaimer && !_.isEmpty(payerNotes?.value)) {
		disclaimers = _.concat(disclaimers, payerNotes.value as string);
	}

	return { descriptions, disclaimers };
};

const getHiSegment = (system: string, hiSegmentUrl: string): string => {
	return _.split(_.replace(system, hiSegmentUrl, ''), '/')[1];
};

// get HI segments
const getDiagnosisCodes = (extensions?: Fhir.Extension[]): EligibilityResponse.IDiagnosisCodes[] => {
	const hiSegmentUrl = systemMapping.HI;
	const hiSegment = _.find(extensions, (extension) => extension?.url === hiSegmentUrl);
	const allSegments = [] as EligibilityResponse.IDiagnosisCodes[];
	if (hiSegment) {
		const hiSegmentCoding = hiSegment.valueCodeableConcept?.coding;
		const groupedSegments = _.groupBy(hiSegmentCoding, ({ system }) => {
			return getHiSegment(system || '', hiSegmentUrl);
		});
		_.map(_.values(groupedSegments), (segment) => {
			const firstSegmentItem = segment[0];
			const secondSegmentItem = segment[1];
			if (firstSegmentItem?.display && secondSegmentItem?.code) {
				allSegments.push({
					diagnosisTypeCode: _.includes(firstSegmentItem.display, hiSegmentMapping.icd9)
						? hiSegmentMapping.icd9
						: hiSegmentMapping.icd10,
					diagnosisCode: secondSegmentItem.code,
				});
			}
		});
	}
	return allSegments;
};

// function to get procedure codes
const getProcedureCodes = (benefits?: Fhir.CoverageEligibilityResponseInsuranceBenefit[]): EligibilityResponse.IParsedCoding => {
	for (const benefit of benefits || []) {
		for (const extension of benefit?.extension || []) {
			if (extension.url === systemMapping.EB) {
				const procedureType = _.find(extension?.valueCodeableConcept?.coding, (item) =>
					_.includes(item?.system, systemMapping['EB-13-01']),
				);
				const procedureCode = _.find(extension?.valueCodeableConcept?.coding, (item) =>
					_.includes(item?.system, systemMapping['EB-13-02']),
				);
				if (procedureType?.code && procedureCode?.code) {
					return { display: `${procedureType?.code}-${procedureCode?.code}`, code: procedureCode?.code };
				}
			}
		}
	}
	return { display: '', code: '' };
};

// collect all procedure code displays to detect HC-G0438/HC-G0439
const hasAwvProcedureCodes = (insuranceItems: Fhir.CoverageEligibilityResponseInsuranceItem[]): boolean => {
	// only check for insurance items which doesn't have any service type
	const allProcedureDisplays = _.compact(
		_.map(insuranceItems, (item) => {
			if (!_.first(item?.productOrService?.coding)?.code) {
				return getProcedureCodes(item?.benefit)?.display;
			}
			return undefined;
		}),
	);
	return _.some(allProcedureDisplays, (display) => _.some(['HC-G0438', 'HC-G0439'], (code) => _.includes(display, code)));
};

export {
	codeMapping,
	getAmount,
	getAddress,
	pickCoding,
	getResource,
	getContacts,
	getHumanName,
	getDatesMeta,
	getNotesMeta,
	getCoveragePath,
	getReferenceMeta,
	getIdentifierMeta,
	getEbSectionData,
	getProcedureCodes,
	getPlaceOfService,
	getDiagnosisCodes,
	getResourceDetails,
	getSortedBenefitData,
	hasAwvProcedureCodes,
	getOtherExtensionData,
	getFhirCoverageResponse,
	updateBenefitDescriptionAndDisclaimer,
	getSecondaryTertiaryPayerAndPrimaryCareProviderDetails,
};
